import { Component } from '@theme/component';

/** Smallest font-size the label may scale down to, in px. */
const MIN_FONT_SIZE = 9;

/** Slack in px so a label exactly as wide as its box is not read as overflowing. */
const SLACK = 0.5;

/**
 * Labels waiting to be fitted. Every label on the page is fitted in one batch with
 * reads and writes kept in separate phases, so a page pays for two layouts per batch
 * instead of up to six per label. Fitting each label on its own made every inline
 * font-size write invalidate layout for the next label's measurement; with 37 pills
 * on a collection grid that was 12–17 s of main-thread time on a throttled phone
 * (docs/handoffs/2026-09-15-sweepstakes-findings.md, R2).
 *
 * @type {Set<SfSweepsLabel>}
 */
const pending = new Set();

/** @type {number} */
let frame = 0;

/**
 * Container elements being watched → the labels inside them.
 *
 * @type {WeakMap<Element, Set<SfSweepsLabel>>}
 */
const watched = new WeakMap();

/**
 * One observer for every label. Entries are gated on width inside the label, so the
 * height change from toggling `data-wrap` can never re-trigger a fit.
 */
const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const labels = watched.get(entry.target);
    if (!labels) continue;
    for (const label of labels) label.containerResized(entry.contentRect.width);
  }
});

/**
 * @param {Element} container
 * @param {SfSweepsLabel} label
 */
const watch = (container, label) => {
  let labels = watched.get(container);
  if (!labels) {
    labels = new Set();
    watched.set(container, labels);
    resizeObserver.observe(container);
  }
  labels.add(label);
};

/**
 * @param {Element} container
 * @param {SfSweepsLabel} label
 */
const unwatch = (container, label) => {
  const labels = watched.get(container);
  if (!labels) return;
  labels.delete(label);
  if (labels.size === 0) {
    watched.delete(container);
    resizeObserver.unobserve(container);
  }
};

/**
 * Fit every pending label on the next frame.
 *
 * Phases: write (reset) → read (measure all) → write (apply all) → read (verify all)
 * → write (nudge or wrap). Each read phase forces at most one layout for the batch.
 */
const flush = () => {
  frame = 0;
  const labels = [...pending].filter((label) => label.isConnected);
  pending.clear();
  if (labels.length === 0) return;

  for (const label of labels) label.reset();
  const measurements = labels.map((label) => label.measure());
  labels.forEach((label, index) => label.apply(measurements[index]));
  const overflowing = labels.map((label, index) => label.overflows(measurements[index]));
  labels.forEach((label, index) => label.finish(overflowing[index]));
};

/** @param {SfSweepsLabel} label */
const schedule = (label) => {
  pending.add(label);
  if (!frame) frame = requestAnimationFrame(flush);
};

/**
 * Keeps the sweepstakes entries label on a single line by scaling its font down
 * to the width its pill makes available.
 *
 * The label length varies per product ("Earn 300 Entries + 150 Bonus" is a
 * third longer than "Earn 690 Entries"), so a fixed size either wraps and grows
 * the pill or truncates with an ellipsis. Base size, padding and line-height
 * stay in the stylesheet; this element only writes an inline font-size, and only
 * when the text would otherwise overflow. It never goes below MIN_FONT_SIZE;
 * a label that still does not fit at that size is flagged with `data-wrap` so
 * the stylesheet lets it break onto a second line instead of cutting it off.
 *
 * Refits when the pill's container changes width, when the text changes (bundle
 * cards rewrite the count on variant change), when web fonts finish loading, and
 * when a Section Rendering API morph strips the inline style. All refits go
 * through the shared batch above.
 *
 * @extends {Component}
 */
class SfSweepsLabel extends Component {
  #controller = new AbortController();

  /** @type {Element | null} */
  #container = null;

  #lastWidth = -1;

  #previousSize = '';

  /**
   * Text changes and a morph stripping the inline style both need a refit. The
   * element's own style writes are discarded with `takeRecords()` right after they
   * are made, so they never feed back into this observer.
   */
  #contentObserver = new MutationObserver(() => schedule(this));

  connectedCallback() {
    super.connectedCallback();

    // The pill itself is fit-content in some variants, so the wrapper is the
    // element whose width tells us when there is room to grow the text back.
    this.#container = this.closest('.ss-banner-wrapper') ?? this.parentElement;
    if (this.#container) watch(this.#container, this);

    this.#contentObserver.observe(this, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    document.fonts?.addEventListener('loadingdone', () => schedule(this), { signal: this.#controller.signal });

    schedule(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#container) unwatch(this.#container, this);
    this.#container = null;
    this.#contentObserver.disconnect();
    this.#controller.abort();
    pending.delete(this);
  }

  /**
   * Called by the shared ResizeObserver. Only a width change can change the fit.
   *
   * @param {number} width
   */
  containerResized(width) {
    if (Math.abs(width - this.#lastWidth) < SLACK) return;
    this.#lastWidth = width;
    schedule(this);
  }

  /** Phase 1 (write): back to the stylesheet size so the measurement starts from the base. */
  reset() {
    this.#previousSize = this.style.fontSize;
    this.style.fontSize = '';
    this.removeAttribute('data-wrap');
    this.#contentObserver.takeRecords();
  }

  /**
   * Phase 2 (read).
   *
   * Fractional geometry, not clientWidth: fit-content pills are exactly as wide as
   * their text, and integer rounding would read that as an overflow.
   *
   * @returns {{ available: number, base: number, needed: number }}
   */
  measure() {
    const { paddingLeft, paddingRight, borderLeftWidth, borderRightWidth, fontSize } = getComputedStyle(this);
    const available =
      this.getBoundingClientRect().width -
      parseFloat(paddingLeft) -
      parseFloat(paddingRight) -
      parseFloat(borderLeftWidth) -
      parseFloat(borderRightWidth);

    // Remember the container width this fit was made for, so the ResizeObserver's
    // initial notification (same width) does not schedule a second, identical fit.
    if (this.#container) this.#lastWidth = this.#container.getBoundingClientRect().width;

    return { available, base: parseFloat(fontSize), needed: this.#textWidth() };
  }

  /**
   * Phase 3 (write): one proportional step. Text width is close to linear in
   * font-size, so a single step lands within glyph rounding of the target.
   *
   * @param {{ available: number, base: number, needed: number }} measurement
   */
  apply({ available, base, needed }) {
    if (available <= 0) {
      // Not rendered right now (closed popover, hidden slide): keep whatever was applied last.
      this.style.fontSize = this.#previousSize;
    } else if (needed > available + SLACK) {
      const estimate = (base * (available - SLACK)) / needed;
      this.style.fontSize = `${Math.max(Math.floor(estimate * 100) / 100, MIN_FONT_SIZE)}px`;
    }
    this.#contentObserver.takeRecords();
  }

  /**
   * Phase 4 (read): does the label still overflow after the proportional step?
   *
   * @param {{ available: number }} measurement
   * @returns {boolean}
   */
  overflows({ available }) {
    return available > 0 && this.#textWidth() > available + SLACK;
  }

  /**
   * Phase 5 (write): glyph rounding can leave a sub-pixel overflow, so step down a
   * little further once; a label that would need to go below the floor wraps instead.
   *
   * @param {boolean} overflowing
   */
  finish(overflowing) {
    if (!overflowing) return;

    const current = parseFloat(this.style.fontSize) || MIN_FONT_SIZE;
    const next = Math.floor(current * 96) / 100;

    if (next > MIN_FONT_SIZE) {
      this.style.fontSize = `${next}px`;
    } else {
      this.style.fontSize = `${MIN_FONT_SIZE}px`;
      this.setAttribute('data-wrap', '');
    }
    this.#contentObserver.takeRecords();
  }

  /**
   * Width of the rendered text, independent of the ellipsis or the box.
   *
   * @returns {number}
   */
  #textWidth() {
    const range = document.createRange();
    range.selectNodeContents(this);
    return range.getBoundingClientRect().width;
  }
}

if (!customElements.get('sf-sweeps-label')) {
  customElements.define('sf-sweeps-label', SfSweepsLabel);
}
